import { Component, OnInit } from '@angular/core';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { Tpl3plService } from '../../../services/tpl3pl.service';

interface UncapturedAuth {
	auth_id: string;
	transid: string;
	orderno: string;
	debtorno: string;
	customer_name: string;
	auth_amount: number;
	auth_status: string;
	transdate: string;
	days_until_expiry: number;
	expiring_soon: boolean;
	invoice_transno: string | null;
	invoice_total: number | null;
	invoice_date: string | null;
	suggested_capture_amount: number;
	mismatch: boolean;
	card_id: string;
	capture_amount?: number;
	capturing?: boolean;
	captured?: boolean;
	capture_error?: string;
}

@Component({
	selector: 'app-tpl3pl-capture-auths',
	templateUrl: './tpl3pl-capture-auths.component.html',
	styleUrls: ['./tpl3pl-capture-auths.component.scss']
})
export class Tpl3plCaptureAuthsComponent implements OnInit {
	items: UncapturedAuth[] = [];
	loading = false;
	preauthEnabled = true;
	totalItems = 0;
	showCaptured = false;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {}

	ngOnInit(): void {
		this.load();
	}

	load(): void {
		this.loading = true;
		this.tpl3plService.listUncapturedAuths().subscribe({
			next: (res) => {
				this.loading = false;
				this.preauthEnabled = !!res.preauth_enabled;
				const raw: UncapturedAuth[] = res.items || [];
				this.items = raw.map(item => ({
					...item,
					capture_amount: item.suggested_capture_amount,
					capturing: false,
					captured: false,
					capture_error: ''
				}));
				this.totalItems = res.total || this.items.length;
			},
			error: () => {
				this.loading = false;
				this.globalSearchService.showNotification('Error loading uncaptured authorizations', 'danger', 'bottom', 'right');
			}
		});
	}

	get visibleItems(): UncapturedAuth[] {
		return this.showCaptured ? this.items : this.items.filter(i => !i.captured);
	}

	get pendingCount(): number {
		return this.items.filter(i => !i.captured).length;
	}

	get mismatchCount(): number {
		return this.items.filter(i => i.mismatch && !i.captured).length;
	}

	get expiringCount(): number {
		return this.items.filter(i => i.expiring_soon && !i.captured).length;
	}

	get noInvoiceCount(): number {
		return this.items.filter(i => !i.invoice_transno && !i.captured).length;
	}

	capture(item: UncapturedAuth): void {
		if (item.capturing || item.captured) { return; }
		const amount = item.capture_amount ?? item.suggested_capture_amount;
		if (!amount || amount <= 0) {
			this.globalSearchService.showNotification('Enter a valid capture amount', 'warning', 'bottom', 'right');
			return;
		}
		item.capturing = true;
		item.capture_error = '';
		this.tpl3plService.captureAuth({
			transid: item.transid,
			auth_id: item.auth_id,
			orderno: item.orderno,
			amount
		}).subscribe({
			next: (res) => {
				item.capturing = false;
				if (res.success) {
					item.captured = true;
					this.globalSearchService.showNotification(
						`Captured ${this.fmt(amount)} for order ${item.orderno}`,
						'success', 'bottom', 'right'
					);
				} else {
					item.capture_error = res.message || 'Capture failed';
					this.globalSearchService.showNotification(item.capture_error, 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				item.capturing = false;
				item.capture_error = err?.error?.message || 'Capture failed';
				this.globalSearchService.showNotification(item.capture_error, 'danger', 'bottom', 'right');
			}
		});
	}

	captureAll(): void {
		const pending = this.items.filter(i => !i.captured && !i.capturing && !!i.invoice_transno);
		if (pending.length === 0) {
			this.globalSearchService.showNotification('No invoiced items to capture', 'warning', 'bottom', 'right');
			return;
		}
		pending.forEach(item => this.capture(item));
	}

	fmt(v: number | null | undefined): string {
		if (v == null) { return '—'; }
		return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	}

	statusClass(item: UncapturedAuth): string {
		if (item.captured) { return 'ca-row-captured'; }
		if (item.capture_error) { return 'ca-row-error'; }
		if (item.expiring_soon) { return 'ca-row-expiring'; }
		if (item.mismatch) { return 'ca-row-mismatch'; }
		if (!item.invoice_transno) { return 'ca-row-noinvoice'; }
		return '';
	}

	authStatusLabel(status: string): string {
		const s = (status || '').toLowerCase().trim();
		if (s === '1' || s === 'approved') { return 'Approved'; }
		if (s === 'requires_capture') { return 'Requires capture'; }
		return status;
	}
}
